import { useLocation } from "react-router-dom";
import { ArrowLeft, Construction } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/core/LanguageContext";

const moduleInfo: Record<string, { title: string; description: string; key: string }> = {
  "/startup": {
    key: "startup",
    title: "Digital Startup App",
    description: "Equipment startup work instructions, verification steps, and pre-production readiness checks.",
  },
  "/instructions": {
    key: "instructions",
    title: "Work Instructions",
    description: "Startup work instructions and pre-production verification steps.",
  },
  "/quality-checks": {
    key: "qualityChecks",
    title: "Digital Quality Check App",
    description: "In-process quality inspections and final product quality verification workflows.",
  },
  "/defects": {
    key: "defects",
    title: "Digital Defect Tagging App",
    description: "Capture, categorize, and track defects with visual tagging and root cause analysis.",
  },
  "/shift-reports": {
    key: "shiftReports",
    title: "Digital Shift Report & Handover",
    description: "Document shift activities, issues, and handover notes for seamless transitions.",
  },
  "/performance": {
    key: "performance",
    title: "Digital Performance View",
    description: "Real-time OEE, FPY, scrap rate, and cycle time analytics and trending.",
  },
  "/competency": {
    key: "competency",
    title: "Competency Management",
    description: "Skills matrix, training records, certifications, and competency tracking.",
  },
  "/8d-management": {
    key: "eightD",
    title: "8D Management",
    description: "Supplier quality management with 8D problem-solving methodology.",
  },
  "/customer-complaints": {
    key: "customerComplaints",
    title: "Customer Complaints",
    description: "Track complaints raised by customers and manage resolution workflows.",
  },
  "/audits": {
    key: "audits",
    title: "Layered Process Audits",
    description: "Plan, execute, and track layered process audits across production areas.",
  },
  "/energy": {
    key: "energy",
    title: "Energy Management",
    description: "Monitor energy consumption, analyze efficiency, and track sustainability metrics across production.",
  },
};

export default function ModulePage() {
  const location = useLocation();
  const { t } = useLanguage();
  const info = moduleInfo[location.pathname] || { title: "Module", description: "Module description", key: "module" };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link to="/">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t("module.back", "Back to Dashboard")}
        </Button>
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t(`dashboard.module.${info.key}.title`, info.title)}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t(`dashboard.module.${info.key}.description`, info.description)}
        </p>
      </div>

      {/* Placeholder Content */}
      <div className="bg-card rounded-xl shadow-card border border-border p-12 text-center">
        <div className="w-16 h-16 rounded-2xl gradient-industrial flex items-center justify-center mx-auto mb-4 shadow-industrial">
          <Construction className="w-8 h-8 text-primary-foreground" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t("module.placeholder.title", "Module Under Development")}
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t(
            "module.placeholder.description",
            "This module is currently being built. Check back soon for full functionality including forms, data tables, and reporting features."
          )}
        </p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="industrial">{t("module.placeholder.request", "Request Feature")}</Button>
          <Button variant="outline">{t("module.placeholder.docs", "View Documentation")}</Button>
        </div>
      </div>

      {/* Feature Preview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg p-5 border border-border">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
            <span className="text-primary font-bold">1</span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">
            {t("module.preview.dataEntry", "Data Entry")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("module.preview.dataEntry.desc", "Digital forms with validation and auto-save")}
          </p>
        </div>
        <div className="bg-card rounded-lg p-5 border border-border">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
            <span className="text-primary font-bold">2</span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">
            {t("module.preview.analytics", "Analytics")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("module.preview.analytics.desc", "Real-time dashboards and trend analysis")}
          </p>
        </div>
        <div className="bg-card rounded-lg p-5 border border-border">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
            <span className="text-primary font-bold">3</span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">
            {t("module.preview.reports", "Reports")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("module.preview.reports.desc", "Automated report generation and export")}
          </p>
        </div>
      </div>
    </div>
  );
}
